// Authentication Service for AAR Guest Management System
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updateProfile,
  OAuthProvider,
  signInWithPopup,
  getAdditionalUserInfo
} from "firebase/auth";
import { doc, getDoc, setDoc, deleteDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "./config";
import { isAdminEmail, getRoleName } from "../utils/roles";
import { getViewAs, setViewAsSession, clearViewAsSession } from "../utils/viewAs";
import { logAction, LOG_CATEGORY, LOG_STATUS } from "./activityLog";

/**
 * Login with email and password
 * @param {string} email
 * @param {string} password
 * @returns {Promise<Object>} User credential
 */
export const loginWithEmail = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);

    // Update last login timestamp
    const userRef = doc(db, "users", userCredential.user.uid);
    await setDoc(userRef, {
      lastLogin: serverTimestamp()
    }, { merge: true });

    return userCredential;
  } catch (error) {
    console.error("Login error:", error);
    throw error;
  }
};

/**
 * Register new user with email and password
 * @param {string} email
 * @param {string} password
 * @param {string} displayName
 * @returns {Promise<Object>} User credential
 */
export const registerWithEmail = async (email, password, displayName) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);

    // Update display name
    await updateProfile(userCredential.user, {
      displayName: displayName
    });

    // Create user document in Firestore
    const userRef = doc(db, "users", userCredential.user.uid);
    await setDoc(userRef, {
      email: email,
      displayName: displayName,
      createdAt: serverTimestamp(),
      lastLogin: serverTimestamp(),
      provider: "email"
    });

    // Check if user is an admin, otherwise assign default role
    const defaultRole = isAdminEmail(email) ? "sys_admin" : "aar_volunteer";
    const roleRef = doc(db, "userRoles", userCredential.user.uid);
    await setDoc(roleRef, {
      role: defaultRole,
      assignedAt: serverTimestamp(),
      assignedBy: "system"
    });

    return userCredential;
  } catch (error) {
    console.error("Registration error:", error);
    throw error;
  }
};

/**
 * Login with Microsoft OAuth
 * @returns {Promise<Object>} User credential
 */
export const loginWithMicrosoft = async () => {
  try {
    const provider = new OAuthProvider('microsoft.com');
    provider.setCustomParameters({
      tenant: process.env.REACT_APP_AZURE_TENANT_ID || 'common'
    });
    provider.addScope('User.Read');

    const userCredential = await signInWithPopup(auth, provider);

    // Resolve the user's email robustly. Azure AD only populates the `mail`
    // claim (which Firebase maps to user.email) when the account has an
    // Exchange mailbox; for many accounts user.email is null and the address
    // lives in userPrincipalName / preferred_username on the raw profile, or
    // on providerData. Falling back here prevents a valid (and possibly
    // hard-coded admin) user from being wrongly rejected as NOT_AUTHORIZED.
    const additionalInfo = getAdditionalUserInfo(userCredential);
    const profile = additionalInfo?.profile || {};
    const signedInEmail = (
      userCredential.user.email ||
      userCredential.user.providerData?.[0]?.email ||
      profile.email ||
      profile.mail ||
      profile.preferred_username ||
      profile.userPrincipalName ||
      profile.upn ||
      ''
    ).toLowerCase();
    const isHardcodedAdmin = !!signedInEmail && isAdminEmail(signedInEmail);

    // Authorization gate: a signed-in M365 user must be (a) a hard-coded admin,
    // (b) explicitly invited via the admin portal (allowedUsers entry), or
    // (c) already onboarded previously (existing userRoles doc). M365 group
    // membership is no longer used — access is controlled manually here.
    let allowedUserData = null;
    if (signedInEmail) {
      try {
        const allowedSnap = await getDoc(doc(db, "allowedUsers", signedInEmail));
        if (allowedSnap.exists()) {
          allowedUserData = allowedSnap.data();
        }
      } catch (lookupErr) {
        console.warn('allowedUsers lookup failed:', lookupErr?.code || lookupErr?.message);
      }
    }

    const userRef = doc(db, "users", userCredential.user.uid);
    const userDoc = await getDoc(userRef);

    let existingRole = null;
    try {
      const roleSnap = await getDoc(doc(db, "userRoles", userCredential.user.uid));
      if (roleSnap.exists()) existingRole = roleSnap.data();
    } catch (e) {
      console.warn('userRoles lookup failed:', e?.code || e?.message);
    }

    if (!isHardcodedAdmin && !allowedUserData && !existingRole) {
      await signOut(auth);
      throw new Error('NOT_AUTHORIZED');
    }

    if (!userDoc.exists()) {
      await setDoc(userRef, {
        email: userCredential.user.email || signedInEmail,
        displayName: userCredential.user.displayName || profile.name || signedInEmail,
        createdAt: serverTimestamp(),
        lastLogin: serverTimestamp(),
        provider: "microsoft"
      });

      // Role precedence: hard-coded admin email > allowedUsers entry > default
      let assignedRole;
      if (isHardcodedAdmin) {
        assignedRole = "sys_admin";
      } else if (allowedUserData?.role) {
        assignedRole = allowedUserData.role;
      } else {
        assignedRole = "aar_leader";
      }
      const roleRef = doc(db, "userRoles", userCredential.user.uid);
      await setDoc(roleRef, {
        role: assignedRole,
        assignedAt: serverTimestamp(),
        assignedBy: allowedUserData ? (allowedUserData.addedBy || "system") : "system"
      });
    } else {
      await setDoc(userRef, {
        lastLogin: serverTimestamp()
      }, { merge: true });
    }

    return userCredential;
  } catch (error) {
    console.error("Microsoft login error:", error);
    throw error;
  }
};

/**
 * Logout current user
 * @returns {Promise<void>}
 */
export const logout = async () => {
  try {
    await endViewAs({ reload: false });
    await signOut(auth);
  } catch (error) {
    console.error("Logout error:", error);
    throw error;
  }
};

/**
 * Send password reset email
 * @param {string} email
 * @returns {Promise<void>}
 */
export const resetPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error) {
    console.error("Password reset error:", error);
    throw error;
  }
};

/**
 * Get user role from Firestore
 * @param {string} uid
 * @returns {Promise<string>} User role
 */
export const getUserRole = async (uid) => {
  try {
    const roleRef = doc(db, "userRoles", uid);
    const roleDoc = await getDoc(roleRef);

    if (roleDoc.exists()) {
      // Existing doc — respect whatever role is stored. The auto-upgrade
      // for admin emails only runs at bootstrap (no doc yet) so a sys_admin
      // can intentionally assign themselves a different role for testing
      // without the next page load reverting it.
      return roleDoc.data().role;
    }

    // Bootstrap: doc doesn't exist. If this is a hardcoded admin email,
    // create their role doc with sys_admin. Rules allow create when
    // request.auth.uid == userId, so this works even before any role is set.
    // Only for the signed-in user's own uid — never for someone being viewed
    // via "View as", which would otherwise create a sys_admin role for them.
    const user = auth.currentUser;
    if (user && user.uid === uid && user.email && isAdminEmail(user.email)) {
      await setDoc(roleRef, {
        role: "sys_admin",
        assignedAt: serverTimestamp(),
        assignedBy: "system"
      });
      return "sys_admin";
    }

    return "aar_volunteer"; // Default role
  } catch (error) {
    console.error("Get user role error:", error);
    return "aar_volunteer";
  }
};

/**
 * Subscribe to auth state changes
 * @param {Function} callback
 * @returns {Function} Unsubscribe function
 */
export const subscribeToAuthState = (callback) => {
  return onAuthStateChanged(auth, callback);
};

/**
 * Get current user — the member being viewed while a sys_admin is using
 * "View as", otherwise the signed-in user. Every screen resolves "who am I /
 * what's my role" through here, so view-as needs no per-screen changes. Code
 * that must act as the real person (audit logging, auth) uses getRealUser().
 * @returns {Object|null} Current user
 */
export const getCurrentUser = () => {
  const real = auth.currentUser;
  if (!real) return null;
  const viewAs = getViewAs(real.uid);
  if (!viewAs) return real;
  return {
    uid: viewAs.uid,
    email: viewAs.email || null,
    displayName: viewAs.displayName || null,
    isViewAs: true
  };
};

/**
 * The actually signed-in user, ignoring "View as".
 * @returns {Object|null}
 */
export const getRealUser = () => auth.currentUser;

// ==================== VIEW AS ====================

const viewAsSessionRef = (adminUid) => doc(db, "viewAsSessions", adminUid);

/**
 * Start viewing the app as another member (sys_admin only). Writes the
 * server-side session doc first — that is what makes Firestore rules and
 * Cloud Functions refuse this admin's writes — then switches the app over.
 * @param {{uid: string, email?: string, displayName?: string, role?: string}} target
 */
export const startViewAs = async (target) => {
  const real = auth.currentUser;
  if (!real) throw new Error('Not signed in');
  if (!target?.uid || target.uid === real.uid) throw new Error('Choose another user to view as');
  if ((await getUserRole(real.uid)) !== 'sys_admin') {
    throw new Error('Only system administrators can view the app as another user');
  }

  const targetRole = await getUserRole(target.uid);
  const targetName = target.displayName || target.email || target.uid;
  await setDoc(viewAsSessionRef(real.uid), {
    targetUid: target.uid,
    targetName,
    targetEmail: target.email || null,
    targetRole,
    startedAt: serverTimestamp()
  });

  // Logged before switching so the entry is plainly the admin's own action.
  logAction(LOG_CATEGORY.ADMIN, 'view_as_started', LOG_STATUS.INFO,
    `Started viewing as ${targetName} (${getRoleName(targetRole)})`,
    { targetUid: target.uid, targetRole });

  setViewAsSession({
    adminUid: real.uid,
    uid: target.uid,
    email: target.email || null,
    displayName: targetName,
    role: targetRole,
    startedAt: new Date().toISOString()
  });
  window.location.assign('/dashboard');
};

/**
 * Stop viewing as another member and return to the admin's own role.
 * @param {{reload?: boolean}} [opts]
 */
export const endViewAs = async ({ reload = true } = {}) => {
  const real = auth.currentUser;
  const session = real ? getViewAs(real.uid) : null;
  clearViewAsSession();
  if (real) {
    try {
      await deleteDoc(viewAsSessionRef(real.uid));
    } catch (err) {
      // Left behind if offline; syncViewAsSession() removes it on next load.
      console.warn('Could not remove view-as session:', err?.code || err?.message);
    }
    if (session) {
      logAction(LOG_CATEGORY.ADMIN, 'view_as_ended', LOG_STATUS.INFO,
        `Stopped viewing as ${session.displayName || session.email}`,
        { targetUid: session.uid });
    }
  }
  if (reload) window.location.assign('/administration');
};

/**
 * Reconcile the browser's view-as state with the server session doc once the
 * signed-in user is known. Removes a leftover server session (so the admin is
 * not stuck read-only), and drops a local session the server no longer backs
 * (so the app never shows view-as without the server-side write block).
 * @param {Object|null} realUser
 * @returns {Promise<void>}
 */
export const syncViewAsSession = async (realUser) => {
  if (!realUser) return;
  const local = getViewAs(realUser.uid);
  let serverExists = false;
  try {
    serverExists = (await getDoc(viewAsSessionRef(realUser.uid))).exists();
  } catch {
    return; // offline — leave things as they are; the client guard still applies
  }

  if (!local && getViewAs()) {
    // A session belonging to a different account on this browser.
    clearViewAsSession();
  }
  if (!local && serverExists) {
    await deleteDoc(viewAsSessionRef(realUser.uid)).catch(() => {});
  } else if (local && !serverExists) {
    clearViewAsSession();
    window.location.reload();
  }
};
